import { Fragment, useState } from 'react';
import type {
  PortalClubwiseBlock,
  PortalClubwiseChannel,
  PortalClubwiseChannelSplit,
} from '../../../../../types/portalReport';
import { JOINED_REPEAT_NOTE } from '../../../../../lib/clubwise/report';
import { foldTop } from '../../../../../lib/clubwise/channelSplit';
import { numberFull, percent, DASH } from './format';

// CLIENT PORTAL — "Where the enquiries came from", by CHANNEL TYPE (ClubWise).
//
// ⚠️⚠️ AGGREGATES ONLY: leads, joined and a conversion per channel. Nothing here
// can show a member.
//
// ⚠️ It replaces the Internet / everywhere-else bar for ClubWise brands and keeps
// its look: one bar, then the keyed breakdown. ⚠️ EVERY LEAD HAS EXACTLY ONE
// CHANNEL, so the rows sum to the total; a lead from an upload made before
// channels were stored belongs to NONE of the rows and is counted and named
// below, never folded into "Offline".
//
// ⚠️ `joined` is a COHORT figure — this period's leads who have joined SO FAR — so
// it rises as later months are uploaded. The note says so under the table, since
// a reader who sees it climb a month later would otherwise think it was wrong.
//
// ⚠️ Colours are fixed tokens on the always-dark hero band; they identify a row
// in this one table and mean nothing elsewhere (they are NOT the Meta / Google
// channel colours, and not volt).

const SEGMENT_COLOURS = [
  'var(--rpt-kelly)',
  'var(--rpt-cream)',
  'var(--rpt-baseline)',
  'var(--rpt-marine)',
  'var(--rpt-petrol)',
] as const;

export type SplitView = 'sources' | 'contactTypes';

/**
 * The expanded panel under a channel row: a two-option toggle, then sub-rows in
 * the SAME table columns as the parent (Leads / Joined / Conversion). Presentational:
 * the state lives in the table so this renders from props in a server test.
 * ⚠️ Sub-rows add up to the parent (the split is a fold of the same rows).
 */
export function ChannelSplitRows({
  channel,
  split,
  view,
  showAll,
  onView,
  onShowAll,
}: {
  channel: PortalClubwiseChannel;
  split: PortalClubwiseChannelSplit;
  view: SplitView;
  showAll: boolean;
  onView?: (v: SplitView) => void;
  onShowAll?: () => void;
}) {
  const { top, rest, other } = foldTop(view === 'sources' ? split.sources : split.contactTypes);
  const shown = showAll ? [...top, ...rest] : top;
  const cells = (r: { leads: number; joined: number; conversionRate: number | null }) => (
    <>
      <td>{numberFull(r.leads)}</td>
      <td>{numberFull(r.joined)}</td>
      <td>{r.conversionRate === null ? DASH : percent(r.conversionRate)}</td>
    </>
  );
  return (
    <>
      <tr className="rpt-channel-split" data-testid={`channel-split-${channel}`}>
        <td colSpan={4}>
          <div role="group" aria-label="Split channel by" className="rpt-switch">
            {([
              ['sources', 'By source'],
              ['contactTypes', 'By contact type'],
            ] as const).map(([v, label]) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                className={view === v ? 'rpt-switch-on' : undefined}
                onClick={() => onView?.(v)}
              >
                {label}
              </button>
            ))}
          </div>
        </td>
      </tr>
      {shown.map((r) => (
        <tr key={r.label} className="rpt-channel-sub" data-testid="channel-sub-row">
          <th scope="row">{r.label}</th>
          {cells(r)}
        </tr>
      ))}
      {other && !showAll && (
        <tr className="rpt-channel-sub" data-testid="channel-sub-other">
          <th scope="row">
            <button type="button" aria-expanded={false} onClick={onShowAll}>
              {other.label}
            </button>
          </th>
          {cells(other)}
        </tr>
      )}
    </>
  );
}

export function ClubwiseChannelTable({
  block,
  showBar = true,
  initialOpen = null,
}: {
  block: PortalClubwiseBlock;
  showBar?: boolean;
  /** Test seam: render with a channel already expanded. */
  initialOpen?: PortalClubwiseChannel | null;
}) {
  // One channel open at a time.
  const [open, setOpen] = useState<PortalClubwiseChannel | null>(initialOpen);
  const [view, setView] = useState<SplitView>('sources');
  const [showAll, setShowAll] = useState(false);
  const total = block.total.leads;
  if (block.measuredMonths.length === 0) return null;
  return (
    <div className="rpt-nest" data-testid="clubwise-channels">
      {showBar && total > 0 && (
        <div className="rpt-nest-track rpt-nest-track--segments" role="img" aria-label="Leads by channel type">
          {block.channels.map((c, i) =>
            c.leads > 0 ? (
              <div
                key={c.channel}
                className="rpt-nest-seg"
                style={{ width: `${(c.leads / total) * 100}%`, background: SEGMENT_COLOURS[i] }}
              />
            ) : null,
          )}
        </div>
      )}
      <table className="rpt-table rpt-channel-table">
        <thead>
          <tr>
            <th scope="col">Channel</th>
            <th scope="col">Leads</th>
            <th scope="col">Joined</th>
            <th scope="col">Conversion</th>
          </tr>
        </thead>
        <tbody>
          {block.channels.map((c, i) => {
            const split = block.channelSplits?.[c.channel];
            const expandable = !!split && c.leads > 0;
            const isOpen = expandable && open === c.channel;
            return (
              <Fragment key={c.channel}>
                <tr>
                  <th scope="row">
                    {expandable ? (
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={`channel-split-${c.channel}`}
                        onClick={() => {
                          setOpen(isOpen ? null : c.channel);
                          setView('sources');
                          setShowAll(false);
                        }}
                      >
                        <span aria-hidden="true">{isOpen ? '▾' : '▸'}</span>{' '}
                        <span className="rpt-swatch" style={{ background: SEGMENT_COLOURS[i] }} /> {c.label}
                      </button>
                    ) : (
                      <>
                        <span className="rpt-swatch" style={{ background: SEGMENT_COLOURS[i] }} /> {c.label}
                      </>
                    )}
                  </th>
                  <td>{numberFull(c.leads)}</td>
                  <td>{numberFull(c.joined)}</td>
                  <td>{c.conversionRate === null ? DASH : percent(c.conversionRate)}</td>
                </tr>
                {isOpen && split && (
                  <ChannelSplitRows
                    channel={c.channel}
                    split={split}
                    view={view}
                    showAll={showAll}
                    onView={(v) => {
                      setView(v);
                      setShowAll(false);
                    }}
                    onShowAll={() => setShowAll(true)}
                  />
                )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td>{numberFull(block.total.leads)}</td>
            <td>{numberFull(block.total.joined)}</td>
            <td>{block.total.conversionRate === null ? DASH : percent(block.total.conversionRate)}</td>
          </tr>
        </tfoot>
      </table>
      <p className="rpt-sub rpt-muted">{JOINED_REPEAT_NOTE}</p>
      {block.unclassifiedLeads > 0 && (
        <p className="rpt-sub rpt-muted">
          {`${numberFull(block.unclassifiedLeads)} leads were uploaded before channels were recorded and are in the total only. Re-uploading those months will place them.`}
        </p>
      )}
    </div>
  );
}
